/**
 * @architecture-header 1
 * @layer module
 * @domain reports
 * @responsibility Render the per-student approval queue for one finished run.
 * @state-owner dataset store holds approvals and comments
 * @allowed-imports React; dataset and workspace stores; shared components, selectors and hooks; module dialogs
 * @forbidden direct API client; student-only surfaces
 * @public-api workspace main column
 * @side-effects approval updates, guardian send checks and toasts
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useOverlay } from '../../../shared/components/OverlayHost';
import { GroupHeading, Page, PageBody, PageHeader } from '../../../shared/components/PageShell';
import { StatusText } from '../../../shared/components/StatusIcon';
import { RUN_MODE_LABEL } from '../../../shared/lib/demo-data';
import { clickable } from '../../../shared/lib/interactive';
import {
  awaitsTeacher,
  findRun,
  markTally,
  runName,
  scoreAnswers,
} from '../../../shared/lib/selectors';
import { answerKey, selectRunReport, useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { GuardianSendCheckDialog, guardianState } from '../../classrooms';
import { buildRunComment, resolvePrompt } from '../model/comment';
import { ActivityInsightDialog } from './ActivityInsightDialog';
import { PromptDialog } from './PromptDialog';

export function RunReview({ runId }: { runId: string }) {
  const t = useTranslator();
  const { openModal } = useOverlay();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const runs = useDatasetStore((state) => state.runs);
  const answers = useDatasetStore((state) => state.answers);
  const report = useDatasetStore((state) => selectRunReport(state, runId));
  const prompts = useDatasetStore((state) => state.prompts.run);
  const guardians = useDatasetStore((state) => state.guardians);
  const open = useWorkspaceUiStore((state) => state.open);
  const openItem = useWorkspaceUiStore((state) => state.openItem);
  const navigate = useWorkspaceUiStore((state) => state.navigate);
  const notify = useWorkspaceUiStore((state) => state.notify);

  const run = findRun(runs, runId);
  if (!run) return null;
  const students = classes[run.classIndex].students;
  /**
   * A sheet is ready for approval once the teacher has looked at it, or straight away when nothing
   * on it was left for them to judge — then its comment is generated without opening anything.
   */
  const isReady = (student: string) => {
    const sheet = answers[answerKey(runId, student)];
    return Boolean(sheet && (sheet.reviewed || !awaitsTeacher(sheet.marks)));
  };
  const approvedCount = students.filter((student) => report.approved[student]).length;
  const reviewedCount = students.filter(isReady).length;
  const expanded = open?.k === 'rev' ? (open.stu ?? null) : null;
  const promptSettings = resolvePrompt('run', prompts);

  const toggleApproval = (student: string, close = false) => {
    if (!isReady(student)) {
      notify(t('請先檢視作答再核准'));
      return;
    }
    useDatasetStore.getState().setReportApproved(runId, student, !report.approved[student]);
    if (close) openItem({ k: 'rev', id: runId, stu: null });
  };

  const approveAll = () => {
    const store = useDatasetStore.getState();
    const missing = students.filter((student) => !isReady(student));
    if (missing.length) notify(`${missing.length} ${t('人尚未檢視作答')}`);
    for (const student of students) {
      if (isReady(student)) store.setReportApproved(runId, student, true);
    }
  };

  const sendAll = () => {
    const names = students.filter((student) => report.approved[student]);
    const deliver = () => {
      const deliverable = names.filter(
        (student) => guardianState(guardians[student] ?? []) === 'linked',
      ).length;
      useDatasetStore.getState().updateRun(runId, { sent: true });
      notify(`${t('已發送')} ${deliverable} ${t('筆給家長')}`);
    };
    const missing = names.filter((student) => guardianState(guardians[student] ?? []) !== 'linked');
    if (!missing.length) {
      deliver();
      return;
    }
    openModal(<GuardianSendCheckDialog names={names} onContinue={deliver} />);
  };

  const sticking: readonly (readonly [string, number, string, readonly string[]])[] = [
    [t('移項變號'), 3, 'var(--error)', [students[2], students[6], students[10]]],
    [t('合併同類項'), 2, '#d98d09', [students[4], students[7]]],
  ];

  return (
    <Page maxWidth={1000}>
      <PageHeader
        actions={
          <>
            <button
              className="bt"
              type="button"
              onClick={() => openModal(<ActivityInsightDialog runId={runId} />)}
            >
              {t('作業洞察')}
            </button>
            <button className="bt pri" disabled={!approvedCount} type="button" onClick={sendAll}>
              {`${t('發送報告')}（${approvedCount}）`}
            </button>
          </>
        }
        meta={`${classes[run.classIndex].name} · ${t(RUN_MODE_LABEL[run.mode])} · ${students.length} ${t('人')}`}
        title={`${t(run.dateLabel)} · ${runName(activities, run)}`}
      />
      <div className="vtbar">
        <span className="vsumline">
          {t('已檢視作答')} <b>{reviewedCount}</b> {` / ${students.length} `}
          <em>·</em> {t('已核准')} <b>{approvedCount}</b> {` / ${students.length}`}
        </span>
      </div>
      <PageBody>
        <div className="dgrid" style={{ margin: '16px 0 22px' }}>
          <div className="dcard">
            <h6>{t('完成')}</h6>
            <b className="big">{students.length}</b>
            <span>{t('全部交齊')}</span>
          </div>
          <div className="dcard">
            <h6>{t('AI 自己解決')}</h6>
            <b className="big">9</b>
            <span>{t('次提示後學生自行修正')}</span>
          </div>
          <div className="dcard">
            <h6>{t('你介入')}</h6>
            <b className="big">1</b>
            <span>{t('次真人接手')}</span>
          </div>
        </div>

        <GroupHeading>{t('這次的共同卡點')}</GroupHeading>
        {sticking.map(([label, count, color, faces]) => (
          <div className="cmini" key={label}>
            <span>{label}</span>
            <span className="cv3">
              <i style={{ width: `${count * 20}%`, background: color }} />
            </span>
            <span className="vfaces">
              {faces.filter(Boolean).map((name) => (
                <span className="vfa" key={name} title={name}>
                  {name[0]}
                </span>
              ))}
            </span>
            <em>{`${count} ${t('人')}`}</em>
          </div>
        ))}

        <div className="vgrph">
          {t('逐份核准')}
          <span className="c2">{`${approvedCount} / ${students.length}`}</span>
          <div className="sp" />
          <button
            className="lk"
            type="button"
            onClick={() => openModal(<PromptDialog kind="run" />)}
          >
            {t('提示詞')}
          </button>
          <button className="lk" type="button" onClick={approveAll}>
            {t('全部核准')}
          </button>
        </div>

        <div className="vrvlist">
          {students.map((student) => {
            const sheet = answers[answerKey(runId, student)];
            const approved = Boolean(report.approved[student]);
            const isOpen = expanded === student;
            const score = sheet ? scoreAnswers(sheet.marks) : null;
            const tally = sheet ? markTally(sheet.marks) : null;
            const ready = isReady(student);
            return (
              <div className={`vrv ${approved ? 'ok' : ''} ${isOpen ? 'op' : ''}`} key={student}>
                {/* biome-ignore lint/a11y/useSemanticElements: the row hosts its own buttons, so it cannot itself be a button */}
                <div
                  className="vrvh"
                  role="button"
                  tabIndex={0}
                  {...clickable(() =>
                    openItem({ k: 'rev', id: runId, stu: isOpen ? null : student }),
                  )}
                >
                  <button
                    className={`vtck ${approved ? 'on' : ''}`}
                    disabled={!ready}
                    style={{ opacity: 1 }}
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleApproval(student);
                    }}
                  >
                    {approved ? '✓' : ''}
                  </button>
                  <span className="vfa2">{student[0]}</span>
                  <span className="vrvt">
                    <b>{student}</b>
                    <span>
                      {ready && score
                        ? `${sheet?.reviewed ? t('已檢視作答') : t('已自動批改')} · ${score.correct}/${score.total}`
                        : t('尚未檢視')}
                    </span>
                  </span>
                  {tally ? (
                    <span className="vtally">
                      <em className="ok">{`${t('答對')} ${tally.ok}`}</em>
                      <em className="fix">{`${t('訂正')} ${tally.fix}`}</em>
                      <em className="no">{`${t('答錯')} ${tally.no}`}</em>
                      {tally.low ? <em className="low">{`${t('待確認')} ${tally.low}`}</em> : null}
                    </span>
                  ) : null}
                  {ready ? (
                    <StatusText glyph="done" label={t('可核准')} />
                  ) : (
                    <StatusText glyph="warn" label={t('待檢視')} tone="wn" />
                  )}
                  <button
                    className="bt sm"
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      openItem({ k: 'ans', id: runId, stu: student });
                    }}
                  >
                    {t('檢視作答')}
                  </button>
                  <em className="vrvx">{isOpen ? t('收合') : t('展開')}</em>
                </div>
                {isOpen ? (
                  <div className="vrvb">
                    {ready && sheet ? (
                      <>
                        <textarea
                          className="vcmt"
                          defaultValue={
                            report.comments[student] ??
                            buildRunComment(student, sheet.marks, promptSettings, t)
                          }
                          key={`${runId}-${student}`}
                        />
                        <div className="rowbt">
                          <button
                            className="bt sm"
                            type="button"
                            onClick={() => openModal(<PromptDialog kind="run" />)}
                          >
                            {t('提示詞')}
                          </button>
                          <button
                            className="bt sm"
                            type="button"
                            onClick={() =>
                              useDatasetStore
                                .getState()
                                .setReportComment(
                                  runId,
                                  student,
                                  buildRunComment(student, sheet.marks, promptSettings, t),
                                )
                            }
                          >
                            {t('重新生成')}
                          </button>
                          <button
                            className="bt sm"
                            type="button"
                            onClick={() => navigate('rep', student)}
                          >
                            {t('檢視學習報告')}
                          </button>
                          <button
                            className="bt sm pri"
                            type="button"
                            onClick={() => toggleApproval(student, true)}
                          >
                            {t('核准')}
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="vguide">
                        <b>{t('請先檢視作答再生成評語')}</b>
                        <span>{t('評語依據你判定的對錯自動生成，請先確認每一題。')}</span>
                        <button
                          className="bt pri"
                          type="button"
                          onClick={() => openItem({ k: 'ans', id: runId, stu: student })}
                        >
                          {t('檢視作答狀況')}
                        </button>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </PageBody>
    </Page>
  );
}
